import React from "react";
import {
  TextField,
  CircularProgress,
  Button,
  Typography,
  Box,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import PropTypes from "prop-types";
import { getBlanketEmailHelperText } from "../../shared/formHelpers";

const defaultFormatSSN = (ssn) => {
  if (!ssn || ssn.length < 4) return ssn;
  return "XXX-XX-" + ssn.slice(-4);
};

const BlanketBasicInformationBase = ({
  config,
  control,
  errors,
  watch,
  setValue,
  loadingCounties,
  allCounties,
  allCities,
  allStates,
  otherResidency,
  handleOtherResidencyChange,
  emailExists,
  emailChecking,
  emailDialogOpen,
  handleDialogClose,
  handleDialogContinue,
  handleDialogCancel,
  handleGoogleCallback,
  residencyStatus: residencyStatusProp,
  handleEmailFieldChange,
  emailDebounceRef,
  professionalGuardians,
  loadingGuardians,
  formatSSN: formatSSNProp,
}) => {
  const {
    GoogleAutoCompleteComponent,
    showProfessionalGuardianLink,
    residencyStatusSource,
    emailHandler,
    formatSSNMode,
  } = config;

  const residencyStatus =
    residencyStatusSource === "watch" ? watch("residencyStatus") : residencyStatusProp;

  const formatSSN =
    formatSSNMode === "prop" ? formatSSNProp : defaultFormatSSN;

  const handleEmailChange = (value, fieldOnChange) => {
    if (emailHandler === "fieldChange") {
      handleEmailFieldChange(value, fieldOnChange);
      return;
    }
    fieldOnChange(value);
    emailDebounceRef.current(value, setValue);
  };

  return (
    <Box className="grid grid-cols-1 gap-4" data-testid="bond-wizard-step-basic">
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="firstName"
            control={control}
            defaultValue=""
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                fullWidth
                required
                variant="outlined"
                error={!!errors.firstName}
                helperText={errors.firstName ? "First Name is required" : ""}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="middleName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Middle Name"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="lastName"
            control={control}
            defaultValue=""
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                fullWidth
                required
                variant="outlined"
                error={!!errors.lastName}
                helperText={errors.lastName ? "Last Name is required" : ""}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="preferredName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Preferred Name"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="dob"
            control={control}
            defaultValue=""
            rules={{ required: "Date of birth is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Date of Birth"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    max: new Date().toISOString().split("T")[0],
                  },
                }}
                fullWidth
                variant="outlined"
                required
                error={!!errors.dob}
                helperText={errors.dob ? "Date of Birth is required" : ""}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="county"
            control={control}
            defaultValue=""
            rules={{ required: "County is required" }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allCounties}
                getOptionLabel={(option) => option || ""}
                loading={loadingCounties}
                onChange={(event, newValue) => {
                  setValue("county", newValue || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Home County"
                    fullWidth
                    required
                    variant="outlined"
                    error={!!errors.county}
                    helperText={errors.county ? "County is required" : ""}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCounties ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
        {residencyStatus === "Other" && (
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Please specify"
              fullWidth
              variant="outlined"
              value={otherResidency}
              onChange={handleOtherResidencyChange}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(0, 0, 0, 0.6)",
              marginBottom: "4px",
              fontWeight: 500,
            }}
          >
            Search Address
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GoogleAutoCompleteComponent
              placeholder="Search Address"
              callback={handleGoogleCallback}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="address"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Street Address"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="zipCode"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="ZIP/Postal Code"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="city"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allCities}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    fullWidth
                    variant="outlined"
                    error={!!errors.city}
                    helperText={errors.city ? "City is required" : ""}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="state"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allStates}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    fullWidth
                    variant="outlined"
                    error={!!errors.state}
                    helperText={errors.state ? "State is required" : ""}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  required
                  variant="outlined"
                  error={!!errors.email || emailExists}
                  helperText={getBlanketEmailHelperText(
                    emailChecking,
                    emailExists,
                    errors.email
                  )}
                  onChange={(e) => {
                    handleEmailChange(e.target.value, field.onChange);
                  }}
                />
                <Dialog
                  open={emailDialogOpen}
                  onClose={(_, reason) => {
                    if (reason !== "backdropClick") handleDialogClose();
                  }}
                >
                  <DialogTitle>Warning</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      This email already exists for an existing user in the
                      Probate Bond Pros System. Would you like to continue with
                      this email?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDialogCancel} color="secondary">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDialogContinue}
                      color="primary"
                      autoFocus
                    >
                      Continue
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
            rules={{ required: true }}
          />
        </Grid>

        {showProfessionalGuardianLink && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="selectedProfessionalGuardian"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <Autocomplete
                  options={professionalGuardians}
                  loading={loadingGuardians}
                  value={field.value}
                  isOptionEqualToValue={(option, value) =>
                    option?.pg_id === value?.pg_id
                  }
                  getOptionLabel={(option) => option?.label || ""}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link to existing Professional Guardian (optional)"
                      placeholder="Search by AMS customer #, name, or email"
                      fullWidth
                      variant="outlined"
                      helperText="For historic bonds: select an existing guardian to link the bond and prevent creating a duplicate account."
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingGuardians ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>
            Will you prefer to e-sign the Indemnity Agreement?
          </Typography>
          <Controller
            name="is_esignatures"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography>May we text you?</Typography>
          <Controller
            name="mayWeText"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 9 }}>
          <Controller
            name="cell_phone"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <InputMask
                mask="999-999-9999"
                value={field.value}
                onChange={field.onChange}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="Cell Phone"
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="123-456-7890"
                    error={!!errors.cell_phone}
                    helperText={
                      errors.cell_phone ? "Cell Phone is required" : ""
                    }
                  />
                )}
              </InputMask>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography>Are you a U.S. Citizen?</Typography>
          <Controller
            name="usCitizen"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Controller
            name="ssn"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Social Security Number"
                type="password"
                fullWidth
                required
                variant="outlined"
                error={!!errors.ssn}
                helperText={
                  errors.ssn ? "SSN is required" : formatSSN(field.value)
                }
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Box
          sx={{
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            backgroundColor: "#f9fafb",
            padding: "16px",
            margin: "16px 0",
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: "2px" }}>
            Authorization to Obtain Underwriting Information
          </Typography>
          <Typography>
            Do you fully understand and agree, by submitting this request for
            approval you are providing authorization for Probate Bond Pros,
            surety companies and/or third-party brokers to verify information
            you provided as well as permission to obtain your consumer
            information from any source which would be necessary to determine
            bonding eligibility? This includes obtaining credit reports at time
            of application, during any review or renewal, at the time of any
            potential or actual claim, and/or for any other legitimate purposes
            determined by the surety in its reasonable discretion. This
            information will be held in the strictest confidence. Do you agree?
          </Typography>

          <Controller
            name="authorizationAgreement"
            control={control}
            rules={{ required: "You must select an option" }}
            render={({ field }) => (
              <FormControl
                error={!!errors.authorizationAgreement}
                component="fieldset"
              >
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {errors.authorizationAgreement && (
                  <FormHelperText error>
                    {errors.authorizationAgreement.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
      </Grid>
    </Box>
  );
};

BlanketBasicInformationBase.propTypes = {
  config: PropTypes.shape({
    GoogleAutoCompleteComponent: PropTypes.elementType.isRequired,
    showProfessionalGuardianLink: PropTypes.bool.isRequired,
    residencyStatusSource: PropTypes.oneOf(["watch", "prop"]).isRequired,
    emailHandler: PropTypes.oneOf(["fieldChange", "debounceRef"]).isRequired,
    formatSSNMode: PropTypes.oneOf(["inline", "prop"]).isRequired,
  }).isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  watch: PropTypes.func,
  setValue: PropTypes.func.isRequired,
  loadingCounties: PropTypes.bool,
  allCounties: PropTypes.array,
  allCities: PropTypes.array,
  allStates: PropTypes.array,
  otherResidency: PropTypes.any,
  handleOtherResidencyChange: PropTypes.func.isRequired,
  emailExists: PropTypes.bool,
  emailChecking: PropTypes.bool,
  emailDialogOpen: PropTypes.bool,
  handleDialogClose: PropTypes.func.isRequired,
  handleDialogContinue: PropTypes.func.isRequired,
  handleDialogCancel: PropTypes.func.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  residencyStatus: PropTypes.any,
  handleEmailFieldChange: PropTypes.func,
  emailDebounceRef: PropTypes.object,
  professionalGuardians: PropTypes.array,
  loadingGuardians: PropTypes.bool,
  formatSSN: PropTypes.func,
};

export default BlanketBasicInformationBase;
